<script lang="ts">
	import * as Dialog from '@ui-components/ui/dialog/index';
	import IonIcon from '@ui-components/ui/IonIcon.svelte';
	import { Button } from '@ui-components/ui/button';

	interface Props {
		open?: boolean;
		featureName?: string;
		onOpenChange?: (open: boolean) => void;
	}

	let { open = false, featureName = 'Recording and transcription', onOpenChange }: Props = $props();

	// Local state to track open/close for Dialog.Root binding
	let isOpen = $state(open);

	// Sync isOpen with prop changes
	$effect(() => {
		isOpen = open;
	});

	// Notify parent when dialog closes
	$effect(() => {
		if (!isOpen && open) {
			onOpenChange?.(false);
		}
	});

	function handleDownload() {
		window.location.href = '/download';
		isOpen = false;
		onOpenChange?.(false);
	}
</script>

<Dialog.Root bind:open={isOpen}>
	<Dialog.Content class="overflow-hidden border-0 bg-white p-0 dark:bg-gray-950 sm:max-w-2xl">
		<!-- Hero Section -->
		<div
			class="relative bg-gradient-to-br from-blue-50 via-white to-teal-50 px-8 pb-6 pt-8 dark:from-gray-900 dark:via-gray-950 dark:to-teal-950"
		>
			<div class="flex flex-col items-center gap-4 text-center">
				<div class="font-['Instrument_Sans'] text-3xl font-bold text-gray-900 dark:text-white">
					{featureName} available on your Mac
				</div>
				<p class="max-w-md font-['Instrument_Sans'] text-lg text-gray-600 dark:text-gray-400">
					Experience the full power of AI-enhanced productivity with our native macOS app
				</p>
			</div>
		</div>

		<!-- Features Grid -->
		<div class="bg-white px-8 py-6 dark:bg-gray-950">
			<div class="grid grid-cols-3 gap-6">
				<div class="flex flex-col items-center gap-2 text-center">
					<div class="rounded-full bg-teal-100 p-3 dark:bg-teal-900">
						<IonIcon name="microphone_fill" myClass="size-6 text-teal-600 dark:text-teal-400" />
					</div>
					<p class="text-sm font-medium text-gray-900 dark:text-gray-100">
						Capture Zoom®, Google Meet™, Teams®, and more
					</p>
				</div>
				<div class="flex flex-col items-center gap-2 text-center">
					<div class="rounded-full bg-blue-100 p-3 dark:bg-blue-900">
						<IonIcon name="sparkles" myClass="size-6 text-blue-600 dark:text-blue-400" />
					</div>
					<p class="text-sm font-medium text-gray-900 dark:text-gray-100">
						AI Transcription & Knowledge Base
					</p>
				</div>
				<div class="flex flex-col items-center gap-2 text-center">
					<div class="rounded-full bg-purple-100 p-3 dark:bg-purple-900">
						<IonIcon
							name="checkmark_shield"
							myClass="size-6 text-purple-600 dark:text-purple-400"
						/>
					</div>
					<p class="text-sm font-medium text-gray-900 dark:text-gray-100">Private & Secure</p>
				</div>
			</div>
		</div>

		<!-- Download Button -->
		<div class="flex flex-col items-center gap-3 bg-white px-8 pb-6 dark:bg-gray-950">
			<Button
				variant="download"
				size="download"
				class="cursor-pointer !px-5 !py-2.5 !text-base"
				onclick={handleDownload}
			>
				<IonIcon myClass="text-white size-4" name="apple_logo" />
				Download for Mac
			</Button>
			<p class="font-['Instrument_Sans'] text-sm text-gray-500 dark:text-gray-400">
				Currently available on macOS 15+ with M1 chips or later
			</p>
			<p class="px-4 text-center font-['Instrument_Sans'] text-xs text-gray-400 dark:text-gray-500">
				Zoom® is a registered trademark of Zoom Video Communications, Inc. Google Meet™ is a
				trademark of Google LLC. Teams® is a registered trademark of Microsoft Corporation.
			</p>
		</div>
	</Dialog.Content>
</Dialog.Root>
